import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AddressAutocomplete,
  StateCodeSelect,
} from '@/components/ui/address-autocomplete';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Description,
  Field,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Switch, SwitchField } from '@/components/ui/catalyst/switch';
import { Text } from '@/components/ui/catalyst/text';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import type {
  ContactRequestCapabilities,
  DirectoryProviderUnit,
  ProviderContactUnitType,
} from '@/hooks/useProviderDirectory';
import {
  ContactHandleSchema,
  useProvidersAddUnitHandleMutation,
} from '@/hooks/useProviderDirectory';

type ContactType = 'PHONE' | 'FAX' | 'EMAIL' | 'MAILING_ADDRESS';

const CONTACT_TYPE_OPTIONS: {
  label: string;
  value: ContactType;
  description: string;
}[] = [
  {
    label: 'Phone',
    value: 'PHONE',
    description: 'Standard phone line for live calls',
  },
  { label: 'Fax', value: 'FAX', description: 'Fax dedicated to records' },
  {
    label: 'Email',
    value: 'EMAIL',
    description: 'Inbox monitored for medical records',
  },
  {
    label: 'Mail',
    value: 'MAILING_ADDRESS',
    description: 'Physical mailing address for certified mail',
  },
];

const CAPABILITY_OPTIONS: {
  label: string;
  value: ContactRequestCapabilities;
}[] = [
  { label: 'Records only', value: 'RECORDS_ONLY' },
  { label: 'Bills only', value: 'BILLS_ONLY' },
  { label: 'Records and bills', value: 'RECORDS_AND_BILLS' },
];

interface AddContactModalProps {
  providerId: string;
  units: DirectoryProviderUnit[];
  open: boolean;
  onClose: () => void;
  defaultUnitId?: string | null;
  onSuccess?: () => void;
}

interface FormState {
  unitId: string;
  contactType: ContactType;
  value: string;
  name: string;
  notes: string;
  linkNotes: string;
  isPreferred: boolean;
  cadenceDays: string;
  capabilities: ContactRequestCapabilities;
  mailingAddress: {
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    postal_code: string;
  };
}

const DEFAULT_FORM_STATE: FormState = {
  unitId: '',
  contactType: 'PHONE',
  value: '',
  name: '',
  notes: '',
  linkNotes: '',
  isPreferred: false,
  cadenceDays: '',
  capabilities: 'RECORDS_AND_BILLS',
  mailingAddress: {
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
  },
};

export const AddContactModal = ({
  providerId,
  units,
  open,
  onClose,
  defaultUnitId,
  onSuccess,
}: AddContactModalProps) => {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM_STATE);
  const [addHandle, { isLoading }] = useProvidersAddUnitHandleMutation();

  const unitOptions = useMemo(
    () =>
      units.map((unit) => ({
        id: String(unit.id),
        name: unit.name,
        unitType: unit.unit_type as ProviderContactUnitType,
      })),
    [units],
  );

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      ...DEFAULT_FORM_STATE,
      unitId: defaultUnitId || unitOptions[0]?.id || prev.unitId || '',
    }));
  }, [open, defaultUnitId, unitOptions]);

  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateMailingAddress = <K extends keyof FormState['mailingAddress']>(
    key: K,
    value: FormState['mailingAddress'][K],
  ) => {
    setForm((prev) => ({
      ...prev,
      mailingAddress: {
        ...prev.mailingAddress,
        [key]: value,
      },
    }));
  };

  const handleMailingAddressSelect = useCallback(
    (address: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
    }) => {
      setForm((prev) => ({
        ...prev,
        mailingAddress: {
          address_line1: address.address_line1,
          address_line2: address.address_line2 || '',
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
        },
      }));
    },
    [],
  );

  const buildHandlePayload = (): ContactHandleSchema => {
    const base: ContactHandleSchema = {
      handle_type: form.contactType,
      name: form.name || null,
      notes: form.notes || null,
      value: null,
      address_line1: null,
      address_line2: null,
      city: null,
      state: null,
      zip: null,
    };

    if (form.contactType === 'PHONE' || form.contactType === 'FAX') {
      base.value = form.value || null;
    } else if (form.contactType === 'EMAIL') {
      base.value = form.value || null;
    } else if (form.contactType === 'MAILING_ADDRESS') {
      base.address_line1 = form.mailingAddress.address_line1 || null;
      base.address_line2 = form.mailingAddress.address_line2 || null;
      base.city = form.mailingAddress.city || null;
      base.state = form.mailingAddress.state || null;
      base.zip = form.mailingAddress.postal_code || null;
    }

    return base;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.unitId) {
      toast.error('Select a provider unit');
      return;
    }

    if (
      (form.contactType === 'PHONE' ||
        form.contactType === 'FAX' ||
        form.contactType === 'EMAIL') &&
      !form.value.trim()
    ) {
      toast.error('Please enter a value for this contact');
      return;
    }

    const payload = {
      handle: buildHandlePayload(),
      is_preferred: form.isPreferred,
      notes: form.linkNotes || null,
      voice_ai_cadence_days: form.cadenceDays ? Number(form.cadenceDays) : null,
      capabilities: form.capabilities,
    };

    try {
      await addHandle({
        id: providerId,
        unitId: form.unitId,
        createProviderContactRequest: payload,
      }).unwrap();
      toast.success('Contact method added');
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to add provider contact', error);
      toast.error('Unable to add contact method');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="3xl"
      zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
      nested
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogDescription>
          Create a directory contact handle that operators can reuse across
          matters.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <Field>
              <Label>Provider Unit</Label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.unitId}
                onChange={(event) => handleChange('unitId', event.target.value)}
                required
              >
                {unitOptions.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
              <Text className="text-xs text-gray-500 mt-1">
                Choose which department or facility this contact belongs to.
              </Text>
            </Field>

            <Field>
              <Label>Contact Type</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CONTACT_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange('contactType', option.value)}
                    className={`px-3 py-2.5 rounded-md border text-left transition-colors ${
                      form.contactType === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-200'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
            </Field>

            {(form.contactType === 'PHONE' ||
              form.contactType === 'FAX' ||
              form.contactType === 'EMAIL') && (
              <Field>
                <Label>
                  {(() => {
                    if (form.contactType === 'EMAIL') return 'Email Address';
                    if (form.contactType === 'FAX') return 'Fax Number';
                    return 'Phone Number';
                  })()}
                </Label>
                <Input
                  value={form.value}
                  onChange={(event) =>
                    handleChange('value', event.target.value)
                  }
                  placeholder={
                    form.contactType === 'EMAIL'
                      ? 'records@provider.com'
                      : '+1 (555) 123-4567'
                  }
                  required
                />
              </Field>
            )}

            {form.contactType === 'MAILING_ADDRESS' && (
              <div className="space-y-4">
                <Field>
                  <Label>Address Line 1</Label>
                  <AddressAutocomplete
                    value={form.mailingAddress.address_line1}
                    onChange={(value) =>
                      updateMailingAddress('address_line1', value)
                    }
                    onAddressSelect={handleMailingAddressSelect}
                    placeholder="Start typing an address..."
                  />
                </Field>
                <Field>
                  <Label>Address Line 2</Label>
                  <Input
                    value={form.mailingAddress.address_line2}
                    onChange={(event) =>
                      updateMailingAddress('address_line2', event.target.value)
                    }
                    placeholder="Suite, unit, building..."
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field>
                    <Label>City</Label>
                    <Input
                      value={form.mailingAddress.city}
                      onChange={(event) =>
                        updateMailingAddress('city', event.target.value)
                      }
                      placeholder="City"
                      required
                    />
                  </Field>
                  <Field>
                    <Label>State</Label>
                    <StateCodeSelect
                      value={form.mailingAddress.state}
                      onChange={(code) => updateMailingAddress('state', code)}
                      placeholder="Select state"
                    />
                  </Field>
                  <Field>
                    <Label>Postal Code</Label>
                    <Input
                      value={form.mailingAddress.postal_code}
                      onChange={(event) =>
                        updateMailingAddress('postal_code', event.target.value)
                      }
                      placeholder="ZIP / Postal code"
                      required
                    />
                  </Field>
                </div>
              </div>
            )}

            <Field>
              <Label>Contact Name / Label</Label>
              <Input
                value={form.name}
                onChange={(event) => handleChange('name', event.target.value)}
                placeholder="e.g., Main Office, Records Department"
              />
            </Field>

            <Field>
              <Label>Contact Notes (optional)</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(event) => handleChange('notes', event.target.value)}
                placeholder="Any additional context about this contact handle."
              />
            </Field>

            <Field>
              <Label>Internal Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.linkNotes}
                onChange={(event) =>
                  handleChange('linkNotes', event.target.value)
                }
                placeholder="Notes that appear in the directory for internal use."
              />
            </Field>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <SwitchField>
                <div className="flex items-start justify-between gap-6 w-full">
                  <div className="flex-1">
                    <Label
                      htmlFor="directory-primary-toggle"
                      className="text-sm font-medium"
                    >
                      Set as primary contact for records requests
                    </Label>
                    <Description className="mt-1">
                      Primary contacts are prioritized for automated requests.
                      You can change this later.
                    </Description>
                  </div>
                  <Switch
                    id="directory-primary-toggle"
                    checked={form.isPreferred}
                    onChange={(checked) => handleChange('isPreferred', checked)}
                    color="indigo"
                    className="mt-1"
                  />
                </div>
              </SwitchField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <Label>Contact capabilities</Label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.capabilities}
                  onChange={(event) =>
                    handleChange(
                      'capabilities',
                      event.target.value as ContactRequestCapabilities,
                    )
                  }
                >
                  {CAPABILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <Label>Voice AI cadence (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.cadenceDays}
                  onChange={(event) =>
                    handleChange('cadenceDays', event.target.value)
                  }
                  placeholder="Optional"
                />
              </Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            Save Contact
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
